/**
 * TextField — the app's single text input.
 *
 * Replaces the legacy `v-text-field` (29 instances across 11 files). It wraps
 * the design system's Field + Input, so the label/hint/error chrome is the
 * library's; what this adds is the behaviour those 29 call sites actually used:
 * a required marker, a password reveal toggle, numeric bounds, a clear button
 * and icon slots.
 *
 * Deliberate differences from the legacy control:
 *  - `required` is a prop, not a `*` the caller types into the label string.
 *    The component renders the marker AND sets aria-required; legacy did
 *    neither, so screen readers never learned a field was required.
 *  - Errors actually render. The legacy app ships
 *    `.v-input__details { display: none }` in index.html, which hides every
 *    validation message app-wide — rules run and show nothing. Not ported.
 *  - No `variant`/`density` props: legacy read one global constant
 *    (outlined/compact) everywhere, so there was never really a choice.
 */
import { useId, useState } from 'react';

import { Field, Input } from '@components/ui';
import { cx } from '@lib/classNames';

import { ClearIcon, EyeIcon, EyeOffIcon } from '@components/common/icons';

import styles from './TextField.module.css';

export function TextField({
  id,
  label,
  required = false,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  error,
  disabled = false,
  readOnly = false,
  loading = false,
  clearable = false,
  onClear,
  leadingIcon,
  trailingIcon,
  // Interactive controls at the trailing end (e.g. a number stepper). Unlike
  // trailingIcon — decoration, hidden from assistive tech — this is rendered
  // as-is, so buttons in it stay reachable.
  endAdornment,
  min,
  max,
  step,
  hideDetails = false,
  className,
  inputClassName,
  ...rest
}) {
  const reactId = useId();
  const inputId = id ?? `textfield-${reactId}`;
  const [revealed, setRevealed] = useState(false);

  // A password field swaps its own type; everything else passes through.
  const isPassword = type === 'password';
  const resolvedType = isPassword && revealed ? 'text' : type;

  // Clear is only offered when there is something to clear and the field is live.
  const showClear = clearable && !disabled && !readOnly && value != null && value !== '';

  const handleClear = () => {
    onClear?.();
    // Mirror an onChange so a controlled caller needs no extra wiring.
    onChange?.({ target: { value: '' } });
  };

  return (
    <Field
      label={
        label ? (
          <>
            {label}
            {required ? (
              <span className={styles.required} aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </>
        ) : undefined
      }
      hint={hideDetails ? undefined : hint}
      error={hideDetails ? undefined : error}
      htmlFor={inputId}
      className={cx(styles.field, hideDetails && styles.hideDetails, className)}
    >
      <div className={cx(styles.control, disabled && styles.disabled)}>
        {leadingIcon ? (
          <span className={cx(styles.icon, styles.leading)} aria-hidden="true">
            {leadingIcon}
          </span>
        ) : null}

        <Input
          id={inputId}
          type={resolvedType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-busy={loading || undefined}
          min={min}
          max={max}
          step={step}
          className={cx(
            styles.input,
            leadingIcon && styles.hasLeading,
            (showClear || isPassword || trailingIcon || endAdornment) && styles.hasTrailing,
            inputClassName,
          )}
          {...rest}
        />

        <div className={styles.trailing}>
          {showClear ? (
            <button
              type="button"
              className={styles.iconButton}
              onClick={handleClear}
              aria-label={`Clear ${label ?? 'field'}`}
            >
              <ClearIcon />
            </button>
          ) : null}

          {isPassword ? (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? 'Hide password' : 'Show password'}
              aria-pressed={revealed}
              disabled={disabled}
            >
              {revealed ? <EyeIcon /> : <EyeOffIcon />}
            </button>
          ) : null}

          {trailingIcon ? (
            <span className={styles.icon} aria-hidden="true">
              {trailingIcon}
            </span>
          ) : null}

          {endAdornment}
        </div>

        {loading ? <span className={styles.loadingBar} aria-hidden="true" /> : null}
      </div>
    </Field>
  );
}

export default TextField;
